import React, { useState, useRef, useEffect } from 'react';
import AudioPlayer from 'react-h5-audio-player';
import 'react-h5-audio-player/lib/styles.css';
import { useMedia } from '../contexts/MediaContext';

interface Track {
  title: string;
  src: string;
}

const tracks: Track[] = [
  { title: "Wrong Side of Black Mountain (Original)", src: "/audio/wrong-side-black-mountain.m4a" },
  { title: "Moondrop Waltz (Original)", src: "/audio/moondrop-waltz.m4a" },
  { title: "I Cried Again", src: "/audio/i-cried-again.m4a" },
  { title: "Blue Virginia Blues", src: "/audio/blue-virginia-blues.m4a" },
  { title: "She Left Me Standin' on a Mountain", src: "/audio/she-left-me-standing.m4a" },
];

const MusicPlayer: React.FC = () => {
  const [currentTrack, setCurrentTrack] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const { audioRef, currentlyPlaying, setCurrentlyPlaying, stopAllMedia } = useMedia();
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Lazy load when component comes into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          // Start preloading audio files in background after 2 seconds
          setTimeout(() => {
            setAudioLoaded(true);
            // Preload audio files in background
            tracks.forEach((track, index) => {
              // Only preload first 2 tracks initially
              if (index < 2) {
                const audio = new Audio();
                audio.preload = 'none';
                // Set source but don't load yet
                audio.src = track.src;
                // Start loading after a delay
                setTimeout(() => {
                  audio.load();
                }, index * 1000); // Stagger loading
              }
            });
          }, 2000);
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleClickNext = () => {
    setCurrentTrack((currentTrack) =>
      currentTrack < tracks.length - 1 ? currentTrack + 1 : 0
    );
  };

  const handleClickPrevious = () => {
    setCurrentTrack((currentTrack) =>
      currentTrack > 0 ? currentTrack - 1 : tracks.length - 1
    );
  };

  return (
    <div className="space-y-4" ref={containerRef}>
      {/* Track List */}
      <div className="grid gap-2 mb-4">
        {tracks.map((track, index) => (
          <button
            key={index}
            onClick={() => {
              if (currentlyPlaying === 'video') {
                stopAllMedia();
              }
              setCurrentTrack(index);
              setCurrentlyPlaying('audio');
              // Auto-play when track is clicked
              setTimeout(() => {
                if (playerRef.current?.audio?.current) {
                  playerRef.current.audio.current.play();
                }
              }, 100);
            }}
            className={`text-left p-3 rounded-lg transition ${
              currentTrack === index
                ? 'bg-white/20 text-white'
                : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
            }`}
          >
            <span className="font-medium">{track.title}</span>
          </button>
        ))}
      </div>

      {/* Audio Player */}
      <div className="audio-player-wrapper">
        <style>{`
          .audio-player-wrapper .rhap_container {
            background: rgba(0, 0, 0, 0.5);
            border-radius: 0.5rem;
            padding: 1rem;
          }
          .audio-player-wrapper .rhap_main-controls-button,
          .audio-player-wrapper .rhap_volume-button {
            color: white;
          }
          .audio-player-wrapper .rhap_progress-indicator,
          .audio-player-wrapper .rhap_volume-indicator {
            background: white;
          }
          .audio-player-wrapper .rhap_progress-bar,
          .audio-player-wrapper .rhap_volume-bar {
            background: rgba(255, 255, 255, 0.3);
          }
          .audio-player-wrapper .rhap_progress-filled,
          .audio-player-wrapper .rhap_volume-filled {
            background: white;
          }
          .audio-player-wrapper .rhap_time {
            color: rgba(255, 255, 255, 0.8);
          }
        `}</style>
        
        <AudioPlayer
          ref={(element) => {
            playerRef.current = element;
            if (audioRef) {
              audioRef.current = element;
            }
          }}
          src={tracks[currentTrack].src}
          preload="none"
          showJumpControls={false}
          onClickNext={handleClickNext}
          onClickPrevious={handleClickPrevious}
          onEnded={handleClickNext}
          onPlay={() => {
            if (currentlyPlaying === 'video') {
              stopAllMedia();
            }
            setCurrentlyPlaying('audio');
          }}
          header={
            <div className="text-center text-white mb-2">
              Now Playing: {tracks[currentTrack].title}
            </div>
          }
        />
      </div>
    </div>
  );
};

export default MusicPlayer;